import { useState } from "react";
import { Link } from "react-router-dom";
import {
  TicketIcon, Plus, Search, Inbox, Clock, CheckCircle2, XCircle,
  ChevronRight, MessageSquare, AlertCircle,
} from "lucide-react";
import { useGetMyTicketsQuery, Ticket } from "../api/supportApi";
import { StatusBadge, PriorityBadge } from "../components/TicketBadges";

function fmt(d: string) {
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function timeAgo(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return fmt(d);
}

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "open", label: "Open" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

const PRIORITY_ACCENT: Record<string, string> = {
  urgent: "bg-red-500",
  high:   "bg-orange-400",
  medium: "bg-blue-400",
  low:    "bg-gray-300",
};

const PRIORITY_ICON_BG: Record<string, string> = {
  urgent: "bg-red-50 text-red-500",
  high:   "bg-orange-50 text-orange-500",
  medium: "bg-indigo-50 text-indigo-600",
  low:    "bg-gray-100 text-gray-400",
};

export default function MyTicketsPage() {
  const { data: tickets = [], isLoading } = useGetMyTicketsQuery();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const filtered = tickets.filter((t: Ticket) => {
    const matchSearch =
      t.subject.toLowerCase().includes(search.toLowerCase()) ||
      t.reference?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || t.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const stats = [
    { label: "Open",        count: tickets.filter((t: Ticket) => t.status === "open").length,        icon: Inbox,        color: "indigo"  },
    { label: "In Progress", count: tickets.filter((t: Ticket) => t.status === "in_progress").length, icon: Clock,        color: "amber"   },
    { label: "Resolved",    count: tickets.filter((t: Ticket) => t.status === "resolved").length,    icon: CheckCircle2, color: "emerald" },
    { label: "Closed",      count: tickets.filter((t: Ticket) => t.status === "closed").length,      icon: XCircle,      color: "gray"    },
  ];

  const hasUrgent = filtered.some((t: Ticket) => t.status === "open" && t.priority === "urgent");

  return (
    <div className="space-y-5 max-w-5xl mx-auto p-4 md:p-6 pb-20">

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[9px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.count}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-13 h-13 w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-900/20">
              <TicketIcon className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">My Support Tickets</h1>
              <p className="text-gray-400 text-sm mt-1.5">Track and manage your support requests.</p>
            </div>
          </div>
          <Link
            to="/support/tickets/new"
            className="flex items-center gap-2 px-5 py-3 bg-gray-900 text-white text-sm font-black rounded-2xl hover:bg-gray-800 transition-all shadow-lg shadow-gray-900/20 active:scale-95 shrink-0"
          >
            <Plus className="w-4 h-4" />
            New Ticket
          </Link>
        </div>
      </div>

      {/* Urgent banner */}
      {hasUrgent && (
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex items-center gap-3">
          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
          <p className="text-sm text-red-700 font-semibold">
            You have open urgent tickets. Our team has been notified and will respond shortly.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by subject or reference…"
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-900 transition"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setStatusFilter(tab.value)}
              className={`px-4 py-2 rounded-xl text-xs font-black transition-all ${
                statusFilter === tab.value
                  ? "bg-gray-900 text-white shadow-lg shadow-gray-900/20"
                  : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Ticket List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-white rounded-3xl animate-pulse border border-gray-100" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-3xl p-14 text-center">
          <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Inbox className="w-7 h-7 text-gray-400" />
          </div>
          <p className="text-gray-800 font-black text-sm uppercase tracking-tight">
            {search || statusFilter ? "No matching tickets" : "No tickets yet"}
          </p>
          <p className="text-gray-400 text-xs mt-1 mb-5">
            {search || statusFilter ? "Try adjusting your filters" : "Submit a ticket and our team will get back to you."}
          </p>
          {!search && !statusFilter && (
            <Link
              to="/support/tickets/new"
              className="inline-flex items-center gap-2 bg-gray-900 text-white font-black text-sm px-6 py-2.5 rounded-2xl hover:bg-gray-800 transition-colors"
            >
              Create a Ticket <ChevronRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((t: Ticket) => (
            <Link
              key={t.id}
              to={`/support/tickets/${t.id}`}
              className="block bg-white border border-gray-100 rounded-3xl shadow-sm hover:shadow-md transition-all group relative overflow-hidden"
            >
              {/* Priority accent bar */}
              <div className={`absolute left-0 inset-y-0 w-1 ${PRIORITY_ACCENT[t.priority] ?? "bg-gray-200"}`} />

              <div className="pl-6 pr-5 py-5 flex flex-col md:flex-row md:items-center gap-4 justify-between">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 mt-0.5 ${PRIORITY_ICON_BG[t.priority] ?? "bg-gray-100 text-gray-400"}`}>
                    <TicketIcon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-black text-gray-900 text-sm leading-snug group-hover:text-indigo-600 transition-colors">
                      {t.subject}
                    </h3>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{t.reference}</span>
                      {t.category && (
                        <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded font-medium">
                          {t.category.name}
                        </span>
                      )}
                      <span className="text-[10px] text-gray-400">· {timeAgo(t.created_at)}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0 pl-14 md:pl-0">
                  <PriorityBadge priority={t.priority} />
                  <StatusBadge status={t.status} />
                  {(t.replies_count ?? 0) > 0 && (
                    <span className="flex items-center gap-1 text-[10px] text-gray-400 font-bold bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-lg">
                      <MessageSquare className="w-3 h-3" />
                      {t.replies_count}
                    </span>
                  )}
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors ml-1" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-900 rounded-[2.5rem] p-7 text-white">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0">
            <TicketIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-0.5">Support Tips</div>
            <p className="font-semibold text-sm text-white/80 leading-snug">
              Include as much detail as possible when raising a ticket. Our team typically responds within 24 hours.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}
